import React, { useEffect, useMemo } from 'react'
import log from 'loglevel'
import utf8 from 'utf8'
import isString from 'lodash/isString'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { useGoogleDriveContext } from 'src/lib/Linkers/context/GoogleDriveContext'
import { useLoadGoogleApi } from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.hooks'
import { useInvalidateEvidence } from 'src/Utils/swrCacheInvalidation'
import {
  useAlertDialogModal,
  ShowAlertDialog,
} from 'src/components/ModalForms/useAlertDialogModal'
import { useSharepointLinkerShareUrl } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerModal.hooks'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { ModalForm, ModalFormId, ModalFormIdQueryParam } from '../../ModalForm'
import { useCreateEvidence } from '../../../async/suggestions/useCreateEvidence'
import { useBulkCreateEvidence } from '../../../async/evidence/useBulkCreateEvidence'
import {
  EvidenceFormData,
  EvidenceType,
} from './forms/AddEvidenceForm.constants'
import { AddEvidenceForm } from './forms/AddEvidenceForm'
import { AddEvidenceFormDescription } from './forms/AddEvidenceForm.components'

export const AddEvidenceQueryParams = {
  MODAL_FORM_ID: ModalFormIdQueryParam,
  CONTROL_ID: 'controlId',
  REQUEST_ID: 'requestId',
}

type AddEvidenceModalProps = {
  controlId?: string
  requestId?: string
  name: string
  openOnDrag?: boolean
} & (
  | { requestId: string; controlId?: never }
  | { controlId: string; requestId?: never }
)
/**
 * Modal form to add evidence
 */
export const AddEvidenceModal = ({
  controlId,
  requestId,
  name,
  openOnDrag = false,
}: AddEvidenceModalProps): JSX.Element => {
  const runSharepointLinker = useSharepointLinkerShareUrl()
  const googleReady = useLoadGoogleApi(true)
  const showAlerDialog: ShowAlertDialog = useAlertDialogModal()
  const { setFilesSelected, filesSelected, onSubmit } =
    useSharepointFilePickerContext()
  const {
    filesSelected: gdriveFilesSelected,
    setFilesSelected: setGDriveFileSelected,
    setPickerConfig,
    setSource: setGDriveSource,
    setSubjectIds: setGDriveSubjectIds,
    onSubmit: onGDriveSubmit,
  } = useGoogleDriveContext()
  const show = useIsShowModal(ModalFormId.ADD_EVIDENCE)
  const hide = useHideModal({ modalId: ModalFormId.ADD_EVIDENCE })
  const createEvidence = useCreateEvidence({
    controlId,
    requestId,
  })

  const bulkCreateParams = useMemo(() => {
    if (controlId) {
      return { controlId: [controlId] }
    }
    if (requestId) {
      return { requestId: [requestId] }
    }
    return {}
  }, [controlId, requestId])

  const bulkCreateEvidence = useBulkCreateEvidence(bulkCreateParams)
  const invalidateEvidence = useInvalidateEvidence()

  useEffect(() => {
    if (googleReady) {
      setPickerConfig({
        allowMultiselect: true,
        viewId: google.picker.ViewId.DOCS,
        modalId: ModalFormId.ADD_EVIDENCE,
      })
    } else {
      setPickerConfig(null)
    }
  }, [googleReady, setPickerConfig])
  useEffect(() => {
    setGDriveSource('Evidence')
    const subjectIds = []
    if (requestId) {
      subjectIds.push(requestId)
    } else if (controlId) {
      subjectIds.push(controlId)
    }
    setGDriveSubjectIds(subjectIds)
  }, [setGDriveSource, setGDriveSubjectIds, controlId, requestId])

  // TODO: Check everywhere this form is used and make sure it is working for the correct control

  const hideHandler = () => {
    setFilesSelected([])
    setGDriveFileSelected?.([])
    hide()
  }

  const onSubmitFunc = async (
    caption: string,
    formData: EvidenceFormData,
    formDataList: EvidenceFormData[],
    date: Timestamp,
    type: EvidenceType,
    uploadSize = 0,
  ) => {
    let hasError = false
    try {
      if (type === EvidenceType.FILE) {
        await bulkCreateEvidence(
          formDataList.map((f) => f.body as File),
          date,
          uploadSize,
          caption,
        )
      } else if (type === EvidenceType.SHAREPOINTLINK) {
        await runSharepointLinker(
          formData.body.toString(),
          DOCUMENT_TYPE.EVIDENCE,
          !!requestId,
          caption,
          requestId ? [requestId] : controlId ? [controlId] : [],
        )
        await invalidateEvidence()
      } else if (filesSelected.length) {
        await onSubmit({
          caption,
          isViaRequest: !!requestId,
          relevantDate: date,
        })
        await invalidateEvidence()
      } else if ((gdriveFilesSelected || []).length) {
        await onGDriveSubmit({
          caption,
          isViaRequest: !!requestId,
          relevantDate: date,
        })
        await invalidateEvidence()
      } else {
        const { mime, body } = formData
        // Convert string into utf8 encoded codes
        const bodyBytes = new Uint8Array(
          isString(body)
            ? Array.from(utf8.encode(body.trim())).map((s) => s.charCodeAt(0))
            : [],
        )
        const fileName = formData.body instanceof File ? formData.body.name : ''
        // If this fails the user will get a toast
        await createEvidence({
          mime,
          bodyBytes,
          caption: `${caption.trim()}`,
          relevantDate: date,
          fileName,
        })
      }
    } catch (error) {
      hasError = true
      log.error('Error adding evidence', error)
      if (error instanceof Error) {
        showInfoToast(error.message)
      } else {
        showInfoToast('Error adding evidence')
      }
    } finally {
      hideHandler()
      if (hasError && filesSelected.length > 0) {
        showAlerDialog({
          title: 'Unable to Add Evidence',
          body: 'We couldn’t upload your evidence. Please check your permissions or try again. If the problem continues, contact your administrator for help.',
        })
      }
      if (hasError && (gdriveFilesSelected || []).length) {
        showAlerDialog({
          title: 'Unable to Add Evidence',
          body: 'We couldn’t upload your evidence. Please check your permissions or try again. If the problem continues, contact your administrator for help.',
        })
      }
    }
  }

  return (
    <ModalForm
      formId={ModalFormId.ADD_EVIDENCE}
      title={`Add Evidence for ${name}`}
      description={<AddEvidenceFormDescription />}
      openOnDrag={openOnDrag}
      submitText="Add Evidence"
      show={show}
      hide={hideHandler}
      enforceFocus={false}
    >
      <AddEvidenceForm
        onSubmitFunc={onSubmitFunc}
        controlId={controlId}
        modalId={ModalFormId.ADD_EVIDENCE}
        isShown={show}
      />
    </ModalForm>
  )
}
